import React from "react";
import { ArrowRight } from "lucide-react";
import { PILLARS, STRATEGY } from "../mock/data";
import { Wrap, SectionHead, Note } from "./Section";
import { useReveal } from "../hooks/useReveal";

function PillarCard({ item, i }) {
  const [ref, inView] = useReveal();
  const Icon = item.icon;
  return (
    <div ref={ref} className={`reveal ${inView ? "in" : ""} card rounded-2xl p-7`} style={{ animationDelay: `${i * 90}ms` }}>
      <div className="flex items-center justify-between mb-6">
        <span className="mono text-accent text-sm">{item.n}</span>
        <Icon size={22} className="text-accent" strokeWidth={1.6} />
      </div>
      <h3 className="display text-xl mb-1.5">{item.title}</h3>
      {item.sub && <div className="label text-[var(--muted)] mb-4 text-[0.62rem]">{item.sub}</div>}
      <p className="text-[14px] text-[var(--muted)] leading-relaxed">{item.body}</p>
    </div>
  );
}

export default function HowItWorks() {
  return (
    <div id="how">
      <Wrap>
        <SectionHead
          tag="What social trading is"
          title="Professional trades,"
          highlight="executed on your account"
          desc="Social trading means following the activity of an experienced trader. When the strategy opens a position, the same position opens on your account, sized to your capital. When it closes, yours closes. You place no orders and analyse no charts — but the account, the money and the withdrawal rights stay yours."
        />
        <div className="mt-8 card rounded-2xl p-6 max-w-3xl">
          <div className="label text-accent mb-2">The part people get wrong</div>
          <p className="text-[14px] text-[var(--muted)] leading-relaxed">
            Nobody takes custody of your capital. You are not sending money to a fund, a pool or a manager.
            You fund a brokerage account in your own name and grant permission for one strategy's trades to be mirrored into it.
          </p>
        </div>

        <div className="mt-12 grid md:grid-cols-3 gap-5">
          {PILLARS.map((p, i) => <PillarCard key={i} item={p} i={i} />)}
        </div>
      </Wrap>

      <Wrap>
        <SectionHead
          tag="The strategy"
          title="One market. Fixed rules."
          highlight="A modest target."
          desc="Sonic AI is not a black box promising outsized numbers. It trades gold against the dollar on a defined playbook, aiming for 0.5–1% per month on trading volume."
        />
        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {STRATEGY.map((p, i) => <PillarCard key={i} item={p} i={i} />)}
        </div>
        <div className="mt-8 card rounded-2xl p-6 max-w-4xl">
          <p className="text-[14px] text-[var(--muted)] leading-relaxed">
            <span className="text-[var(--text)] font-medium">Read the target carefully.</span> 0.5–1% is measured on trading volume — the amplified figure — not on the amount you deposit. The account structures section shows what that becomes on your own capital.
          </p>
          <a href="#accounts" className="mt-4 inline-flex items-center gap-2 label text-accent hover:gap-3 transition-all">
            See the structures <ArrowRight size={13} />
          </a>
        </div>
      </Wrap>
    </div>
  );
}
